import React from "react";
import Footer from "./Footer";
import Navbar from "./Navbar";
import image1 from "../assets/image1.jpg";

const formatINR = (value: number) => `₹${value.toLocaleString("en-IN")}`;

const LoanIllustrationsAndRates: React.FC = () => {
    const brandName = import.meta.env.VITE_COMPANY_NAME;

    const loanMin = 5000;
    const loanMax = 100000;

    const tenureMin = 7;
    const tenureMax = 40;

    const minSalary = 40000;

    const aprMin = 15;
    const aprMax = 35;

    const processingPct = 10;
    const gstPct = 18;

    const sampleLoan = 10000;
    const sampleDeduction = 1180;
    const sampleNet = 8820;


    const preclosureLabel = "No Charges";
    const prepaymentLabel = "No Charges";

    return (
        <div className="min-w-screen bg-[#141237] text-white py-16 px-4">
            <div className="max-w-7xl mx-auto flex flex-col gap-12">
                <Navbar />

           
                <section className="relative m-auto text-center py-16 rounded-xl shadow-lg px-6 md:px-12 overflow-hidden w-full max-w-3xl">
                    <img
                        src={image1}
                        alt="Loan Illustration and Charges"
                        className="absolute inset-0 w-full h-full object-cover opacity-25"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#141237]/80 via-[#FF2D55]/20 to-[#141237]/80 rounded-xl"></div>

                    <div className="relative z-10">
                        <h1 className="text-4xl sm:text-5xl font-bold mb-4 drop-shadow-lg">
                            Loan Illustrations & Charges
                        </h1>
                        <p className="text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed mt-4 drop-shadow-md">
                            Understand how your{" "}
                            <span className="font-semibold text-[#FF2D55]">{brandName}</span>{" "}
                            loan is structured, including a representative example and a clear
                            breakdown of rates and fees.
                        </p>
                    </div>
                </section>


                <div className="max-w-5xl w-full mx-auto grid gap-8 lg:grid-cols-2">

              
                    <section className="rounded-2xl bg-white text-gray-900 p-6 md:p-8 shadow-md border border-[#FF2D55]/30">
                        <h2 className="text-xl md:text-2xl font-semibold text-center mb-4 text-[#FF2D55]">
                            Rates and Charges
                        </h2>

                        <dl className="space-y-4 text-sm md:text-base">
                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Loan Amount</dt>
                                <dd className="font-medium text-gray-900 text-right">
                                    {formatINR(loanMin)} - {formatINR(loanMax)}
                                </dd>
                            </div>

                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Tenure</dt>
                                <dd className="font-medium text-gray-900 text-right">
                                    {tenureMin} - {tenureMax} Days
                                </dd>
                            </div>

                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">APR</dt>
                                <dd className="font-medium text-gray-900 text-right">
                                    {aprMin}% - {aprMax}%
                                </dd>
                            </div>

                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Processing Fee</dt>
                                <dd className="font-medium text-gray-900 text-right">
                                    ~{processingPct}% of loan amount + GST
                                </dd>
                            </div>

                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Pre-closure Charges</dt>
                                <dd className="font-medium text-green-600 text-right">
                                    {preclosureLabel}
                                </dd>
                            </div>

                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Prepayment Charges</dt>
                                <dd className="font-medium text-green-600 text-right">
                                    {prepaymentLabel}
                                </dd>
                            </div>

                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Min. Salary</dt>
                                <dd className="font-medium text-gray-900 text-right">
                                    {formatINR(minSalary)}
                                </dd>
                            </div>
                        </dl>
                    </section>

  
                    <section className="rounded-2xl bg-white text-gray-900 p-6 md:p-8 shadow-md border border-[#FF2D55]/30">
                        <h2 className="text-xl md:text-2xl font-semibold text-center mb-2 text-[#FF2D55]">
                            Illustrations
                        </h2>
                        <p className="text-center font-medium text-gray-800 mb-2">
                            Representative Example: How Your {brandName} Loan Works
                        </p>
                        <p className="text-center text-sm text-gray-600 mb-4">
                            (Example: {formatINR(sampleLoan)} Loan)
                        </p>

                        <div className="rounded-xl bg-white p-5 md:p-6 shadow-sm border border-gray-100">
                            <p className="text-sm text-gray-700 mb-4 text-center leading-relaxed">
                                When you take a loan of {formatINR(sampleLoan)} from {brandName}
                                , here&apos;s how the charges and disbursal break down.
                            </p>

                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between">
                                    <span className="font-semibold text-gray-800">Processing Fee</span>
                                    <span className="text-gray-800">
                                        {processingPct}% of {formatINR(sampleLoan)}
                                    </span>
                                </div>

                                <div className="flex justify-between">
                                    <span className="font-semibold text-gray-800">GST on Processing Fee</span>
                                    <span className="text-gray-800">{gstPct}% of processing fee </span>
                                </div>

                                <div className="border-t border-gray-200 pt-3 flex justify-between">
                                    <span className="font-semibold text-gray-800">Total Deduction</span>
                                    <span className="text-gray-800">{formatINR(sampleDeduction)}</span>
                                </div>

                                <div className="mt-4 rounded-xl bg-green-50 px-4 py-3 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                                    <span className="font-semibold text-gray-900">Final Amount Disbursed</span>
                                    <span className="font-semibold text-green-600">
                                        {formatINR(sampleLoan)} − {formatINR(sampleDeduction)} = {formatINR(sampleNet)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <Footer />
            </div>
        </div>
    );
};

export default LoanIllustrationsAndRates;
